import { inject, Injectable } from '@angular/core';

import { take } from 'rxjs';

import {
  SkillsIdeateStep,
  SkillsIdeateStepDefinition,
  SkillsIdeateStepI,
} from '@core/tellent-skills-types';
import { DialogRef, DialogService } from '@core/user-interface-legacy';

import { skillsIdeateStepComponentMap } from './skills-ideate-flow-component.map';
import { SkillsIdeateFlowStateService } from './skills-ideate-flow-state.service';

@Injectable({ providedIn: 'root' })
export class SkillsIdeateFlowFacade {
  private readonly dialogService = inject(DialogService);
  private readonly flowStateService = inject(SkillsIdeateFlowStateService);
  private readonly stepComponentMap: Record<SkillsIdeateStep, SkillsIdeateStepI> =
    skillsIdeateStepComponentMap;

  private activeDialogRef?: DialogRef;

  public startFlow(): void {
    this.cancelCurrentFlow();
    const firstStep = this.flowStateService.initializeFlow();
    if (firstStep) {
      this.openStepDialog(firstStep);
    }
  }

  private openStepDialog(stepDef: SkillsIdeateStepDefinition): void {
    const stepToOpen = this.stepComponentMap[stepDef.step];

    if (!stepToOpen) {
      console.error(`No component registered for step: ${stepDef.step}`);
      return;
    }

    this.flowStateService.flowData$.pipe(take(1)).subscribe(data => {
      this.activeDialogRef = this.dialogService.openDialog(
        stepToOpen.dialogComponent,
        { ...data, ...stepDef },
        stepToOpen.dialogConfig,
      );

      this.activeDialogRef.afterClosed.pipe(take(1)).subscribe(result => {
        if (result === undefined) {
          this.activeDialogRef = undefined;
          return;
        }

        if (result?.back) {
          const prevStep = this.flowStateService.goBack();
          if (prevStep) {
            this.openStepDialog(prevStep);
          } else {
            this.activeDialogRef = undefined;
          }
          return;
        }

        const nextStep = this.flowStateService.advanceFlow(result);
        if (nextStep) {
          this.openStepDialog(nextStep);
        } else {
          this.activeDialogRef = undefined;
        }
      });
    });
  }

  private cancelCurrentFlow(): void {
    if (this.activeDialogRef) {
      this.activeDialogRef.close();
      this.activeDialogRef = undefined;
    }
  }
}
